<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useMainStore } from '@/stores/main'
import * as Auth from '@/services/Auth'
import * as Alerts from '@/services/Alerts'
import * as Language from '@/services/Language'
import { pageOfPath } from '@/i18n/pageMeta'

const { t } = useI18n()
const username = ref()
const password = ref()

const store = useMainStore()
const currentRoute = useRoute()
const router = useRouter()

const loggedUserHome = { name: store.config.pages.logged_user_home }

/**
 * Page the login interrupted, from the address the guard put in the query. It is opened by name: the
 * language of the account replaces the one the address was written in, and the router only has the
 * addresses of the language in use (see settleAddress in router/index.js).
 */
function destinationOf(redirectFrom)
{
  if (typeof redirectFrom !== 'string' || !redirectFrom.startsWith('/')) {
    return loggedUserHome
  }

  const url = new URL(redirectFrom, window.location.origin)
  const written = Language.languageOfPath(url.pathname)
  const path = written === null ? url.pathname : (url.pathname.slice(written.length + 1) || '/')
  const page = pageOfPath(written ?? Language.defaultLanguage(), path)

  if (page === null) {
    return loggedUserHome
  }

  return {
    name: page.name,
    params: page.params,
    query: Object.fromEntries(url.searchParams),
    hash: url.hash,
  }
}

// Form state for Vuetify
const loginForm = ref(null)
const formValid = ref(false)
const loading = ref(false)
const showPassword = ref(false)

// Validation rules
const usernameRules = [
  v => !!v || t('auth.validation.emailRequired')
]

const passwordRules = [
  v => !!v || t('auth.validation.passwordRequired')
]

onMounted(() => {
  // Utente già loggato, redirect a pagina principale dell'applicativo.
  if (store.userId){
    router.push(loggedUserHome)
  }
})

async function login()
{
  // Validate form first
  const { valid } = await loginForm.value.validate()
  if (!valid) return

  loading.value = true

  Auth.login({
    email: username.value,
    password: password.value,
  })
  .then(() => {
    // Where the login interrupted the user: the guard puts the page in the query (see
    // router/index.js). Only a path of this site is accepted, anything else goes to the home page
    // of a logged user.
    router.push(destinationOf(currentRoute.query.redirectFrom))
  })
  .catch(error => {
    //console.log("ERROR LOGIN!\n", error.response?.data?.message ?? error);
    Alerts.warning(t('messages.error.loginError'));

  })
  .finally(() => {
    loading.value = false
  });
}
</script>

<template>
  <v-container class="fill-height d-flex align-center justify-center">
    <v-row justify="center">
      <v-col cols="12" sm="6" md="4">
        <v-card class="pa-8" elevation="8">
          <!-- Header -->
          <div class="text-center mb-8">
            <v-icon size="64" color="primary" class="mb-4">mdi-gamepad-variant</v-icon>
            <h1 class="text-h4 mb-2">{{ $t('auth.login.title') }}</h1>
            <p class="text-subtitle-1 text-medium-emphasis">{{ $t('auth.login.subtitle') }}</p>
          </div>

          <!-- Form -->
          <v-form ref="loginForm" v-model="formValid" @submit.prevent="login">
            <v-text-field
              v-model="username"
              :label="$t('auth.login.emailLabel')"
              :rules="usernameRules"
              type="email"
              variant="outlined"
              prepend-inner-icon="mdi-email"
              class="mb-4"
            />

            <v-text-field
              v-model="password"
              :label="$t('auth.login.passwordLabel')"
              :rules="passwordRules"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              prepend-inner-icon="mdi-lock"
              :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append-inner="showPassword = !showPassword"
              class="mb-6"
            />

            <v-btn
              type="submit"
              :loading="loading"
              :disabled="!formValid"
              color="primary"
              size="large"
              block
              class="mb-4"
            >
              {{ $t('auth.login.loginButton') }}
            </v-btn>
          </v-form>

          <!-- Links -->
          <div class="text-center mt-6">
            <p class="text-body-2 my-4">
              {{ $t('auth.login.noAccount') }}<br/>
              <router-link :to="{ name: 'register' }" class="text-primary text-decoration-none">
                {{ $t('auth.login.registerLink') }}
              </router-link>
            </p>
            <p class="text-body-2 my-4">
              {{ $t('auth.login.forgotPassword') }}<br/>
              <router-link :to="{ name: 'forgot_password' }" class="text-primary text-decoration-none">
                {{ $t('auth.login.resetPassword') }}
              </router-link>
            </p>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.fill-height {
  min-height: calc(100vh - 64px);
}
</style>